import { useMemo, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { usePricingCatalogAcquisition } from '../../services/pricingCatalog';
import {
  mapAcquisitionOfferToCadastroPlan,
  hasCatalogCoupomDivergence,
  isPricingCatalogBlockingError as resolveIsPricingCatalogBlockingError,
} from '../Cadastro/plans';

const INTERNAL_IDS_BY_PRICE_ORDER = ['starter', 'master', 'ultimate'];

const formatAmountBRL = value =>
  Number(value).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const mapJussubPlansToCanonicalFormat = planOffers => {
  if (!Array.isArray(planOffers) || planOffers.length === 0) return null;

  const result = [];
  for (const group of planOffers) {
    const type = Number(group.cycleDays) === 365 ? 'annually' : 'monthly';
    const sorted = (group.offers ?? [])
      .map(o => o.plan)
      .filter(Boolean)
      .sort((a, b) => Number(a.amount) - Number(b.amount))
      .slice(0, 3);

    sorted.forEach((plan, i) => {
      result.push({
        id: plan.id,
        internal_id: INTERNAL_IDS_BY_PRICE_ORDER[i],
        name: plan.name,
        amount: formatAmountBRL(plan.amount),
        type,
      });
    });
  }

  return result.length > 0 ? result : null;
};

export const useCheckoutPlans = ({
  isJussubEnabled,
  isJussubResolved,
  coupomCode,
}) => {
  const dispatch = useDispatch();

  const {
    offers: pricingCatalogOffers,
    isLoading: isPricingCatalogLoading,
    isError: isPricingCatalogQueryError,
    errorCode: pricingCatalogErrorCode,
    isCouponRejected: isPricingCatalogCouponRejected,
    refetch: refetchPricingCatalog,
    rejectCoupon,
  } = usePricingCatalogAcquisition({
    coupon: coupomCode || undefined,
    enabled: true,
  });

  const coupom = useSelector(state => state.app?.coupom);
  const isValidateCoupomLoading = useSelector(state => state.app?.is_loading_coupom);

  // um cupom válido em /coupons/validate mas ausente do catálogo novo
  // (legado, nunca migrado) não é mais divergência — ver isCouponMatchedInCatalog. Só resta
  // divergência real quando o catálogo aceita um cupom que /coupons/validate rejeita.
  const hasCoupomDivergence = hasCatalogCoupomDivergence({
    coupomCode,
    isValidateCoupomValid: Boolean(coupom?.is_valid),
    isValidateCoupomLoading: Boolean(isValidateCoupomLoading),
    // o hook cai pro catálogo base ao rejeitar o cupom, então uma rejeição pode
    // já estar com `isError: false`; sem excluir `isPricingCatalogCouponRejected` aqui, um cupom que
    // os dois lados concordam ser inválido seria lido como "aceito pelo catálogo".
    isCatalogCouponAccepted:
      Boolean(coupomCode) &&
      !isPricingCatalogLoading &&
      !isPricingCatalogQueryError &&
      !isPricingCatalogCouponRejected,
  });

  // reusa a mesma função pura de Cadastro/plans.ts: um cupom inválido é
  // entrada do usuário, não falha do catálogo, e não pode bloquear o PlanSummary inteiro
  // (dead-end idêntico ao bloqueador antigo). A divergência também não
  // bloqueia — self-heal via rejectCoupon abaixo, não erro em tela cheia.
  const isPricingCatalogBlockingError = resolveIsPricingCatalogBlockingError({
    isPricingCatalogQueryError,
    pricingCatalogErrorCode,
  });

  // Catálogo aceitou o cupom, mas /coupons/validate (fonte externa a este
  // hook) o rejeita — mesma causa raiz do cupom inválido reportado pelo próprio catálogo, então
  // aciona o mesmo self-heal (dropar o cupom, seguir mostrando o catálogo sem ele) em vez de
  // deixar a divergência bloquear o formulário.
  useEffect(() => {
    if (hasCoupomDivergence && coupomCode) {
      rejectCoupon(coupomCode);
    }
  }, [hasCoupomDivergence, coupomCode, rejectCoupon]);

  const apiCadastroPlans = useMemo(
    () => (pricingCatalogOffers || []).map(mapAcquisitionOfferToCadastroPlan),
    [pricingCatalogOffers],
  );

  const planOffers = useSelector(state => state.app?.checkout_plan_offers);
  const isJussubOffersLoading = useSelector(
    state => state.app?.is_checkout_plan_offers_loading,
  );
  const isJussubOffersLoaded = useSelector(
    state => state.app?.is_checkout_plan_offers_loaded ?? false,
  );
  const isJussubOffersError = useSelector(
    state => state.app?.is_checkout_plan_offers_error ?? false,
  );

  const isPricingResolved = !isPricingCatalogLoading && !isPricingCatalogBlockingError;

  const pricingPlans = useMemo(() => {
    if (!isPricingResolved) return [];
    return apiCadastroPlans;
  }, [isPricingResolved, apiCadastroPlans]);

  useEffect(() => {
    if (isJussubResolved && isJussubEnabled) {
      dispatch({ type: 'LOAD_CHECKOUT_PLAN_OFFERS' });
    }
  }, [isJussubResolved, isJussubEnabled, dispatch]);

  const mappedJussubPlans = useMemo(
    () => mapJussubPlansToCanonicalFormat(planOffers),
    [planOffers],
  );

  // Jussub carregou (sucesso ou falha) e não devolveu nenhum plano usável: sem isso, o hook caía
  // silenciosamente para o catálogo 2026/API, cobrando o cliente errado sem nunca sinalizar erro.
  const isJussubBlockingError =
    isJussubEnabled &&
    isJussubOffersLoaded &&
    (isJussubOffersError || mappedJussubPlans === null);

  const plans = useMemo(() => {
    if (!isPricingResolved || !isJussubResolved) return [];
    if (!isJussubEnabled) return pricingPlans;
    if (isJussubBlockingError) return [];
    return mappedJussubPlans ?? pricingPlans;
  }, [
    isJussubEnabled,
    isJussubResolved,
    mappedJussubPlans,
    pricingPlans,
    isPricingResolved,
    isJussubBlockingError,
  ]);

  const refetch = useCallback(() => {
    refetchPricingCatalog();
    if (isJussubEnabled) {
      dispatch({ type: 'LOAD_CHECKOUT_PLAN_OFFERS' });
    }
  }, [refetchPricingCatalog, isJussubEnabled, dispatch]);

  return {
    plans,
    isLoading:
      !isPricingResolved ||
      !isJussubResolved ||
      (isJussubEnabled && isJussubOffersLoading),
    isLoaded:
      isPricingResolved &&
      isJussubResolved &&
      (!isJussubEnabled || isJussubOffersLoaded),
    isError: isPricingCatalogBlockingError || isJussubBlockingError,
    refetch,
    offers: pricingCatalogOffers,
  };
};
